"use client"

import Image from "next/image"
import { ArrowLeft, ArrowRight, Pause, Play, Search, Send, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { HeroForm, HeroMedia } from "./hero-types"

export function HeroTypewriterIsland({ words, className }: { words: string[]; className?: string }) {
  const [index, setIndex] = useState(0)
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    setReducedMotion(query.matches)
    const handleChange = () => setReducedMotion(query.matches)
    query.addEventListener("change", handleChange)
    return () => query.removeEventListener("change", handleChange)
  }, [])

  useEffect(() => {
    if (reducedMotion || words.length <= 1) return
    const interval = window.setInterval(() => setIndex((current) => (current + 1) % words.length), 2200)
    return () => window.clearInterval(interval)
  }, [reducedMotion, words.length])

  if (words.length === 0) return null
  return <span className={cn("inline-block text-primary transition-opacity", className)}>{words[index]}</span>
}

export function HeroInlineFormIsland({ form, compact = false }: { form: HeroForm; compact?: boolean }) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle")

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setStatus("loading")
    window.setTimeout(() => setStatus("success"), 650)
  }

  return (
    <form action={form.action} onSubmit={handleSubmit} className={cn("grid gap-3 rounded-[1.5rem] border border-border/70 bg-background/84 p-3 shadow-xl backdrop-blur-xl", compact ? "sm:grid-cols-[1fr_auto]" : "sm:grid-cols-[1fr_1fr_auto]")}> 
      {form.fields.map((field) => (
        <label key={field.name} className="grid gap-2 text-sm font-medium text-foreground">
          <span>{field.label}</span>
          <Input type={field.type ?? "text"} name={field.name} placeholder={field.placeholder} required={field.required} className="h-12 rounded-full bg-background" />
        </label>
      ))}
      <Button type="submit" size="lg" className="h-12 self-end rounded-full" disabled={status === "loading"}>
        {status === "loading" ? "Sending" : form.submitLabel ?? "Submit"}
        <Send className="size-4" />
      </Button>
      <p className="min-h-5 text-sm text-muted-foreground sm:col-span-full" aria-live="polite">
        {status === "success" ? form.successMessage ?? "Thanks. We will follow up soon." : status === "error" ? "Something went wrong. Please try again." : ""}
      </p>
    </form>
  )
}

export function HeroSearchIsland({ placeholder = "Search the site" }: { placeholder?: string }) {
  const [query, setQuery] = useState("")
  const suggestions = useMemo(() => ["Services", "Pricing", "Case studies", "Blog", "Contact"].filter((item) => item.toLowerCase().includes(query.toLowerCase())), [query])
  return (
    <div className="relative rounded-[1.5rem] border border-border/70 bg-background/88 p-2 shadow-xl backdrop-blur-xl">
      <label className="flex items-center gap-3 px-3">
        <Search className="size-5 text-muted-foreground" />
        <span className="sr-only">Search</span>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={placeholder} className="h-12 min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground" />
      </label>
      {query && (
        <div className="absolute inset-x-0 top-full z-20 mt-2 rounded-2xl border border-border bg-background p-2 shadow-2xl">
          {suggestions.slice(0, 4).map((suggestion) => <button key={suggestion} type="button" className="block w-full rounded-xl px-3 py-2 text-left text-sm hover:bg-muted">{suggestion}</button>)}
        </div>
      )}
    </div>
  )
}

export function HeroCarouselIsland({ slides }: { slides: HeroMedia[] }) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused || slides.length <= 1) return
    const interval = window.setInterval(() => setIndex((current) => (current + 1) % slides.length), 4200)
    return () => window.clearInterval(interval)
  }, [paused, slides.length])

  if (slides.length === 0) return null
  const active = slides[index]

  return (
    <div className="relative min-h-[24rem] overflow-hidden rounded-[2rem] border border-background/12 bg-foreground text-background shadow-2xl" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} aria-live="polite">
      {active.src && <Image src={active.src} alt={active.alt ?? "Hero carousel slide"} fill sizes="(min-width: 1024px) 48vw, 100vw" className="object-cover" priority />}
      <div className="absolute inset-0 bg-gradient-to-t from-foreground/82 via-foreground/16 to-transparent" />
      <div className="absolute inset-x-4 bottom-4 flex items-center justify-between rounded-full border border-background/16 bg-background/12 p-2 backdrop-blur-xl">
        <Button type="button" size="icon" variant="ghost" className="rounded-full text-background hover:bg-background/12" aria-label="Previous slide" onClick={() => setIndex((current) => (current - 1 + slides.length) % slides.length)}><ArrowLeft /></Button>
        <div className="flex gap-1">
          {slides.map((slide, slideIndex) => <button key={`${slide.src}-${slideIndex}`} type="button" aria-label={`Go to slide ${slideIndex + 1}`} className={cn("size-2 rounded-full bg-background/40", slideIndex === index && "w-6 bg-background")} onClick={() => setIndex(slideIndex)} />)}
        </div>
        <div className="flex items-center gap-1">
          <Button type="button" size="icon" variant="ghost" className="rounded-full text-background hover:bg-background/12" aria-label={paused ? "Play carousel" : "Pause carousel"} onClick={() => setPaused(!paused)}>{paused ? <Play /> : <Pause />}</Button>
          <Button type="button" size="icon" variant="ghost" className="rounded-full text-background hover:bg-background/12" aria-label="Next slide" onClick={() => setIndex((current) => (current + 1) % slides.length)}><ArrowRight /></Button>
        </div>
      </div>
    </div>
  )
}

export function HeroVideoModalIsland({ poster, src, label = "Play video" }: { poster?: string; src?: string; label?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="group absolute inset-0 grid place-items-center bg-foreground/10 text-background" onClick={() => setOpen(true)} aria-label={label}>
        <span className="grid size-20 place-items-center rounded-full bg-background text-foreground shadow-2xl transition-transform group-hover:scale-105"><Play className="ml-1 size-7 fill-current" /></span>
      </button>
      {open && (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-foreground/82 p-4 backdrop-blur-xl" role="dialog" aria-modal="true" aria-label="Hero video">
          <div className="relative aspect-video w-full max-w-5xl overflow-hidden rounded-[2rem] bg-foreground shadow-2xl">
            <button type="button" className="absolute right-4 top-4 z-10 grid size-10 place-items-center rounded-full bg-background text-foreground" aria-label="Close video" onClick={() => setOpen(false)}><X className="size-5" /></button>
            {src ? <video src={src} poster={poster} controls autoPlay className="size-full" /> : <div className="grid size-full place-items-center text-background/70">Video preview</div>}
          </div>
        </div>
      )}
    </>
  )
}

export function HeroBackgroundVideoIsland({ src, poster, captionTrackSrc }: { src: string; poster?: string; captionTrackSrc?: string }) {
  const [paused, setPaused] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    if (query.matches) {
      videoRef.current?.pause()
      setPaused(true)
    }
  }, [])

  function toggle() {
    if (!videoRef.current) return
    if (videoRef.current.paused) {
      videoRef.current.play()
      setPaused(false)
    } else {
      videoRef.current.pause()
      setPaused(true)
    }
  }

  return (
    <>
      <video ref={videoRef} className="absolute inset-0 -z-20 size-full object-cover" autoPlay muted loop playsInline poster={poster} aria-hidden={!captionTrackSrc}>
        <source src={src} />
        {captionTrackSrc && <track kind="captions" src={captionTrackSrc} srcLang="en" label="English captions" />}
      </video>
      <button type="button" className="absolute bottom-4 right-4 z-10 rounded-full border border-background/20 bg-background/12 px-3 py-2 text-xs font-semibold text-background backdrop-blur-xl" onClick={toggle}>{paused ? "Play motion" : "Pause motion"}</button>
    </>
  )
}

export function HeroTiltIsland({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const [style, setStyle] = useState<React.CSSProperties>({})

  function handleMove(event: React.MouseEvent<HTMLDivElement>) {
    if (!ref.current || window.innerWidth < 768) return
    const rect = ref.current.getBoundingClientRect()
    const rotateY = ((event.clientX - rect.left) / rect.width - 0.5) * 8
    const rotateX = ((event.clientY - rect.top) / rect.height - 0.5) * -8
    setStyle({ transform: `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)` })
  }

  return <div ref={ref} style={style} onMouseMove={handleMove} onMouseLeave={() => setStyle({})} className="transition-transform duration-200">{children}</div>
}

export function HeroParallaxIsland({ targetId }: { targetId: string }) {
  useEffect(() => {
    const target = document.getElementById(targetId)
    if (!target) return
    const update = () => {
      if (window.innerWidth < 1024) return
      target.style.transform = `translate3d(0, ${window.scrollY * 0.08}px, 0)`
    }
    update()
    window.addEventListener("scroll", update, { passive: true })
    return () => window.removeEventListener("scroll", update)
  }, [targetId])
  return null
}
